import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import type { Role } from '../api/auth';
import { Icon } from './Icon';
import Illustration from './Illustration';
import RouteSkeleton from './RouteSkeleton';
import { ROLE_KO } from './RoleBadge';

/** 현재 경로로 돌아오는 로그인 링크. */
export function loginHref(next?: string): string {
  return next ? `/login?next=${encodeURIComponent(next)}` : '/login';
}

/**
 * 「접근 권한 필요」 패널 (403).
 *
 * 로그인 안 된 경우는 로그인 링크를, 로그인은 됐지만 역할이 모자라는 경우는
 * 누구 계정인지와 필요한 역할을 보인다 — "왜 안 되는지" 가 화면에 있어야 한다.
 */
export default function AccessDenied({ need, title, message }: {
  need?: Role;
  title?: string;
  message?: string;
}) {
  const { user } = useAuth();
  const loc = useLocation();
  const next = loc.pathname + loc.search;

  return (
    <div className="state empty access-denied" role="alert">
      <Illustration name={user ? 'no-access' : 'login-needed'} />
      {/* 10회차 관찰 ⑥: 화면 최상위 상태라 제목은 h1(보이는 모양은 그대로 — .state-title) */}
      <h1 className="state-title">{title ?? '접근 권한이 필요합니다'}</h1>
      {message ? <p>{message}</p> : user ? (
        <p>
          <strong>{user.display_name}</strong>({ROLE_KO[user.role]}) 계정으로는 볼 수 없습니다.
          {need && <> 필요한 역할: <strong>{ROLE_KO[need]}</strong>.</>}
          <br />권한이 필요하면 관리자에게 요청하세요.
        </p>
      ) : (
        <p>로그인하면 계속할 수 있습니다.</p>
      )}
      <div className="access-denied-actions">
        {!user && <Link className="btn primary sm" to={loginHref(next)}><Icon name="login" size={16} /> 로그인</Link>}
        <Link className="btn sm" to="/"><Icon name="search" size={16} /> 검색으로 돌아가기</Link>
      </div>
    </div>
  );
}

/**
 * 라우트 가드. 비로그인 → /login?next=현재경로. 로그인됐지만 역할 부족 → 403 패널.
 * 세션 확인 중에는 판단하지 않는다(새로고침 직후 잘못 튕기지 않게).
 */
export function RequireAuth({ role, children }: { role?: Role; children: ReactNode }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  // 세션 확인 중에도 글자 대신 그 화면의 골격 — 확인이 끝나면 같은 자리에 화면이 들어온다(9회차 m44)
  if (loading) return <RouteSkeleton pathname={loc.pathname} />;
  if (!user) return <Navigate to={loginHref(loc.pathname + loc.search)} replace />;
  if (role && !allows(user.role, role)) return <AccessDenied need={role} />;
  return <>{children}</>;
}

const RANK: Record<Role, number> = { viewer: 0, operator: 1, admin: 2 };
function allows(have: Role, need: Role): boolean {
  return (RANK[have] ?? -1) >= RANK[need];
}
